import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, ViewEncapsulation } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterModule } from '@angular/router';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { RmaLetModule } from '@rma/dashboard/generic/ui/let';
import { HeapCustomEventType, HeapEventService } from '@rma/dashboard/generic/util/heap';
import { LimitDisplayNumberPipe } from '@rma/dashboard/generic/util/pipes/limit-display-number';
import { UserNotificationModel, UserNotificationType } from '@rma/dashboard/notification-bell/private/api-user-notifications';
import { NotificationItemComponent } from '@rma/dashboard/notification-bell/private/ui-notification-item';
import {
  NotificationBellNavigationService,
  NotificationRouteData,
} from '@rma/dashboard/notification-bell/private/util-notification-content';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { map, Observable, shareReplay, startWith, take, tap } from 'rxjs';
import { NotificationBellFacadeService } from './notifications-bell-facade.service';

@Component({
  selector: 'rma-notification-bell',
  templateUrl: 'notification-bell.component.html',
  styleUrls: ['notification-bell.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    AsyncPipe,
    LimitDisplayNumberPipe,
    MatBadgeModule,
    MatButtonModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    NotificationItemComponent,
    RmaIconComponent,
    RmaLetModule,
    RouterModule,
    TranslocoModule,
  ],
  providers: [provideTranslocoScope('notificationBell')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationBellComponent {
  @HostBinding() class = 'block';

  public notifications$: Observable<UserNotificationModel[]>;
  public total$: Observable<number>;

  public constructor(
    private readonly notificationBellFacadeService: NotificationBellFacadeService,
    private readonly notificationBellNavigationService: NotificationBellNavigationService,
    private readonly heapEventService: HeapEventService,
  ) {
    this.total$ = this.notificationBellFacadeService.unreadNotificationsCount$.pipe(
      shareReplay(),
      tap((notifications) => this.heapTrackNewNotificationsAvailable(notifications)),
    );

    this.notifications$ = this.notificationBellFacadeService.notifications$.pipe(
      map((response) => response.results),
      map((notifications) => notifications.filter((x) => Object.keys(UserNotificationType).includes(x.userNotificationType))),
      tap(() => this.notificationBellFacadeService.markAsViewed()),
    );
  }

  public onNavigate(notification: UserNotificationModel, event: NotificationRouteData) {
    this.notificationBellNavigationService.navigateTo(notification, event);
  }

  public markAllAsRead() {
    this.notificationBellFacadeService.markAllAsRead();
  }

  public heapTrackOpened() {
    this.total$.pipe(startWith(0), take(1)).subscribe((count) => {
      this.heapEventService.trackCustomEvent(HeapCustomEventType.NotificationBellOpened, { newNotificationCount: count });
    });
  }

  private heapTrackNewNotificationsAvailable(count: number) {
    if (count > 0) {
      this.heapEventService.trackCustomEvent(HeapCustomEventType.NotificationBellNewNotificationsAvailable, {
        newNotificationCount: count,
      });
    }
  }
}
